import * as React from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import moment from 'moment'

import { observer } from 'mobx-react'

import DSDayPicker from 'modules/core/components/DayPicker'
import DayPicker from 'modules/core/components/Inputs/DayPicker'
import Select from 'modules/core/components/Inputs/Select'
import Modal from 'modules/core/components/Modal'
import NumberField from 'modules/core/components/NumberField'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import Selector from 'modules/core/components/Selector'
import TextField from 'modules/core/components/TextField'
import UserAvatar from 'modules/core/components/UserAvatar'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import { employeeUser } from 'modules/core/store/collections/users'

import contractVersions from 'modules/contracts/store/collections/contractVersions'
import { LegalEntity } from 'modules/employees/hooks/useLegalEntities'
import { ChangeActionType } from 'modules/employees/pages/EditInBulk/hooks/useChangesForEmployees'
import {
  CustomField,
  CustomFieldValue,
  CustomFieldsWithValues,
  EmployeeGroups,
} from 'modules/employees/pages/EditInBulk/types'
import mapBulkFormToChange from 'modules/employees/pages/EditInBulk/utils/mapBulkFormToChange'
import employees from 'modules/employees/store/collections/employees'
import { employeesThatCanBeMyManager } from 'modules/employees/store/collections/employeesContractVersions'
import type Employee from 'modules/employees/store/models/Employee'
import locations from 'modules/locations/store/collections/locations'
import permissionGroups from 'modules/permissions/store/collections/permissionGroups'
import teams from 'modules/teams/store/collections/teams'

import type { TableSelection } from '../../../hooks/useTableSelection'
import { useDispatchLocalChangeOnEmployees } from '../Context/dispatchLocalChangeOnEmployeesContext'

interface Props {
  tableSelection: TableSelection
  legalEntities: LegalEntity[]
  employeeGroups: EmployeeGroups[]
  onClose: () => void
  customFields: CustomField[]
}

const EditModal = ({
  tableSelection,
  legalEntities,
  employeeGroups,
  onClose,
  customFields,
}: Props) => {
  const dispatchLocalChangeOnEmployees = useDispatchLocalChangeOnEmployees()
  const [selectedManagerEmployeeId, setSelectedManagerEmployeeId] = React.useState<number>()
  const [selectedTimeoffEmployeeId, setSelectedTimeoffEmployeeId] = React.useState<number>()
  const [selectedTeamsIds, setSelectedTeamsIds] = React.useState<number[]>([])
  const [selectedLegalEntityId, setSelectedLegalEntityId] = React.useState<number>()
  const [selectedPermissionGroupId, setSelectedPermissionGroupId] = React.useState<number>()
  const [selectedEmployeeGroupId, setSelectedEmployeeGroupId] = React.useState<number>()
  const [selectedCustomFieldsWithValues, setSelectedCustomFieldsWithValues] =
    React.useState<CustomFieldsWithValues>({})

  const handleChangeManagerSelect = (employeeId: number) => {
    setSelectedManagerEmployeeId(employeeId)
    form.setValues({ manager_id: employeeId })
  }
  const handleChangeTimeOffSupervisorSelect = (employeeId: number) => {
    setSelectedTimeoffEmployeeId(employeeId)
    form.setValues({ timeoff_manager_id: employeeId })
  }

  const handleChangeTeams = (teamIds: number[]) => {
    setSelectedTeamsIds(teamIds)
    form.setValues({ teams_ids: teamIds })
  }

  const handleChangeLegalEntity = (legalEntityId: number) => {
    setSelectedLegalEntityId(legalEntityId)
    setSelectedEmployeeGroupId(undefined)
    form.setValues({ legal_entity_id: legalEntityId })
  }

  const handleChangeEmployeeGroup = (employeeGroupId: number) => {
    setSelectedEmployeeGroupId(employeeGroupId)
    form.setValues({ employee_group_id: employeeGroupId })
  }

  const handleChangePermissionGroup = (permissionGroupId: number) => {
    setSelectedPermissionGroupId(permissionGroupId)
    form.setValues({ permission_group_id: permissionGroupId })
  }

  const handleChangeCustomField = (customFieldId: number, value: CustomFieldValue) => {
    setSelectedCustomFieldsWithValues({ ...selectedCustomFieldsWithValues, [customFieldId]: value })
    form.setValues({ [customFieldId]: value })
  }

  const { performAction } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      dispatchLocalChangeOnEmployees({
        type: ChangeActionType.ADD_CHANGES_TO_TABLE_SELECTION,
        payload: {
          table_selection: tableSelection,
          change: mapBulkFormToChange(form, selectedCustomFieldsWithValues),
        },
      })

      closePortal()
      track(trackingEvents.editEmployeesInBulk.confirmBulkEdit)
    },
    {
      successFlash: false,
    }
  )

  const form = useLazyRef(
    () =>
      new Form(
        {},
        {
          location_id: 'number',
          manager_id: 'number',
          timeoff_policy_id: 'number',
          timeoff_manager_id: 'number',
          regular_access_starts_on: 'date',
          teams_ids: 'any',
          legal_entity_id: 'number',
          permission_group_id: 'number',
          employee_group_id: 'number',
        }
      )
  ).current

  // TODO: Move options logic to a hook
  const locationOptions = locations.map((location) => ({
    label: location.attributes.name,
    value: location.attributes.id,
  }))

  const calculateManagersFor = (
    selectedEmployees: Array<Employee | undefined>,
    allEmployees: Employee[]
  ) => {
    return selectedEmployees.reduce<Employee[]>(
      (acc: Employee[], employee: Employee | undefined) => {
        if (employee) {
          const setEmployeeManager = new Set(
            employeesThatCanBeMyManager(employee, contractVersions)
          )

          // Add new employees from setEmployeeManager that aren't in acc
          const newEmployees = Array.from(setEmployeeManager).filter(
            (newEmployee) => !acc.includes(newEmployee)
          )
          return [...acc, ...newEmployees]
        } else {
          return acc
        }
      },
      allEmployees
    )
  }

  const {
    office,
    manager = [],
    time_off_supervisor = [],
    regular_access_starts_on,
    team,
    legal_entity_and_employee_group,
    permission_group,
    ...customFieldsSelection
  } = tableSelection

  const employeesToUpdateManager = manager.map((id) => employees.get(id))

  const managers = calculateManagersFor(employeesToUpdateManager, employees.models)

  const employeesToUpdateSupervisor = time_off_supervisor.map((id) => employees.get(id))

  const supervisors = calculateManagersFor(employeesToUpdateSupervisor, employees.models)

  const employeeGroupsByLegalEntity = employeeGroups.filter(
    (employeeGroup) => employeeGroup.legalEntityId === selectedLegalEntityId
  )

  const showOffice = !!office?.length
  const showManager = !!manager?.length
  const showTimeOffManager = !!time_off_supervisor?.length
  const showWelcomeSpace = !!regular_access_starts_on?.length
  const showTeams = !!team?.length
  const showLegalEntityAndEmployeeGroup = !!legal_entity_and_employee_group?.length
  const showPermissionGroup = !!permission_group?.length
  const customFieldsSelectionKeys = Object.keys(customFieldsSelection).filter(
    (customFieldKey) => !!customFieldsSelection[customFieldKey]?.length
  )
  const showAnyCustomField = !!customFieldsSelectionKeys.length

  const legalEntityOptions = legalEntities.map((legalEntity) => {
    return (
      <Selector.Option
        key={legalEntity.id}
        value={legalEntity.id}
        label={legalEntity.legalName}
        searchValue={legalEntity.legalName}
      >
        <Box width="full" overflowY="hidden">
          <Text.Normal size="200" weight="medium" ellipsis>
            {legalEntity.legalName}
          </Text.Normal>
        </Box>
      </Selector.Option>
    )
  })

  const employeeGroupOptions = employeeGroupsByLegalEntity?.map((employeeGroup) => {
    return (
      <Selector.Option
        key={employeeGroup.id}
        value={employeeGroup.id}
        label={employeeGroup.name}
        searchValue={employeeGroup.name}
      >
        <Box width="full" overflowY="hidden">
          <Text.Normal size="200" weight="medium" ellipsis>
            {employeeGroup.name}
          </Text.Normal>
        </Box>
      </Selector.Option>
    )
  })

  const teamOptions = teams.models.map((teamOption) => {
    return (
      <Selector.Option
        key={teamOption.id}
        value={teamOption.id}
        label={teamOption.attributes.name}
        searchValue={teamOption.attributes.name}
      >
        <Box width="full" overflowY="hidden">
          <Text.Normal size="200" weight="medium" ellipsis>
            {teamOption.attributes.name}
          </Text.Normal>
        </Box>
      </Selector.Option>
    )
  })

  const permissionGroupOptions = permissionGroups.models.map((permissionGroup) => {
    return (
      <Selector.Option
        key={permissionGroup.id}
        value={permissionGroup.id}
        label={permissionGroup.title}
        searchValue={permissionGroup.title}
      >
        <Box width="full" overflowY="hidden">
          <Text.Normal size="200" weight="medium" ellipsis>
            {permissionGroup.title}
          </Text.Normal>
        </Box>
      </Selector.Option>
    )
  })

  const employeeOptions = (employeesOptions: Employee[]) =>
    employeesOptions.map((employeeOption) => {
      const { attributes } = employeeOption
      const fullName = employeeUser(employeeOption)?.fullName()

      return (
        <Selector.Option
          key={attributes.id}
          value={attributes.id}
          label={fullName}
          searchValue={fullName}
        >
          <UserAvatar size="s32" user={employeeUser(employeeOption)} />
          <Text.Normal>{fullName}</Text.Normal>
        </Selector.Option>
      )
    })

  const customFieldOptions = (customField: CustomField) => {
    const options = customField.options.map((option) => {
      return {
        id: option.id,
        label: option.label,
        isActive: option.isActive,
      }
    })

    return options.map((customFieldOption) => (
      <Selector.Option
        key={customFieldOption.id}
        label={customFieldOption.label || ''}
        searchValue={customFieldOption.label || ''}
        value={customFieldOption.label}
        isDisabled={!customFieldOption.isActive}
      >
        <Box width="full" overflowY="hidden">
          <Text.Normal size="200" weight="medium" ellipsis>
            {customFieldOption.label}
          </Text.Normal>
        </Box>
      </Selector.Option>
    ))
  }

  return (
    <Modal
      title={i18n.t('employees.modify_data')}
      description={i18n.t('employees.modify_data_description')}
      illustration="user-edit"
      onClose={onClose}
      onSubmit={performAction}
      footer={
        <Box alignItems="flexEnd" flexDirection="row" justifyContent="spaceBetween" gap="s8">
          <Button type="primary" submit>
            {i18n.t('employees.confirm')}
          </Button>
        </Box>
      }
    >
      <Box flexDirection="column" gap="s12">
        {showManager && (
          <Box>
            <Selector.Popover
              label={i18n.t('employees.bulk_edit.profile.manager')}
              headerLabel={i18n.t('common.name')}
              blankLabel={i18n.t('common.empty')}
              value={selectedManagerEmployeeId ? [selectedManagerEmployeeId] : []}
              // eslint-disable-next-line no-type-assertion/no-type-assertion
              onChange={(employeeId) => handleChangeManagerSelect(employeeId as number)}
              placeholder={i18n.t('common.select')}
              searchPlaceholder={i18n.t('common.search')}
            >
              {employeeOptions(managers) || []}
            </Selector.Popover>
          </Box>
        )}
        {showOffice && (
          <Select
            none={i18n.t('common.none')}
            label={i18n.t('employees.bulk_edit.profile.office')}
            field={form.get('location_id')}
            options={locationOptions}
          />
        )}
        {showTimeOffManager && (
          <Selector.Popover
            label={i18n.t('employees.bulk_edit.profile.time_off_supervisor')}
            headerLabel={i18n.t('common.name')}
            blankLabel={i18n.t('common.empty')}
            value={selectedTimeoffEmployeeId ? [selectedTimeoffEmployeeId] : []}
            // eslint-disable-next-line no-type-assertion/no-type-assertion
            onChange={(employeeId) => handleChangeTimeOffSupervisorSelect(employeeId as number)}
            placeholder={i18n.t('common.select')}
            searchPlaceholder={i18n.t('common.search')}
          >
            {employeeOptions(supervisors) || []}
          </Selector.Popover>
        )}
        {showTeams && (
          <Selector.Popover
            label={i18n.t('employees.bulk_edit.profile.team')}
            blankLabel={i18n.t('common.empty')}
            allSelectedLabel={i18n.t('common.all')}
            placeholder={i18n.t('common.select')}
            searchPlaceholder={i18n.t('common.search')}
            value={selectedTeamsIds}
            // eslint-disable-next-line no-type-assertion/no-type-assertion
            onChange={(teamIds) => handleChangeTeams(teamIds as number[])}
            onToggle={(isOpen) => !isOpen}
            isMultiSelect
          >
            {teamOptions}
          </Selector.Popover>
        )}
        {showLegalEntityAndEmployeeGroup && (
          <Box flexDirection="row" gap="s16" alignItems="center">
            {legalEntities.length > 1 && (
              <Selector.Popover
                label={i18n.t('employees.bulk_edit.profile.legal_entity')}
                headerLabel={i18n.t('common.name')}
                blankLabel={i18n.t('common.empty')}
                value={selectedLegalEntityId ? [selectedLegalEntityId] : []}
                // eslint-disable-next-line no-type-assertion/no-type-assertion
                onChange={(legalEntityId) => handleChangeLegalEntity(legalEntityId as number)}
                placeholder={i18n.t('common.select')}
                searchPlaceholder={i18n.t('common.search')}
              >
                {legalEntityOptions || []}
              </Selector.Popover>
            )}
            {employeeGroups.length > 1 && (
              <Selector.Popover
                label={i18n.t('employees.bulk_edit.profile.employee_group')}
                blankLabel={i18n.t('common.empty')}
                placeholder={i18n.t('common.select')}
                searchPlaceholder={i18n.t('common.search')}
                value={selectedEmployeeGroupId ? [selectedEmployeeGroupId] : []}
                // eslint-disable-next-line no-type-assertion/no-type-assertion
                onChange={(employeeGroupId) => handleChangeEmployeeGroup(employeeGroupId as number)}
              >
                {employeeGroupOptions}
              </Selector.Popover>
            )}
          </Box>
        )}
        {showPermissionGroup && (
          <Selector.Popover
            label={i18n.t('employees.bulk_edit.profile.permission_group')}
            headerLabel={i18n.t('common.name')}
            blankLabel={i18n.t('common.empty')}
            value={selectedPermissionGroupId ? [selectedPermissionGroupId] : []}
            onChange={(permissionGroupId) =>
              // eslint-disable-next-line no-type-assertion/no-type-assertion
              handleChangePermissionGroup(permissionGroupId as number)
            }
            placeholder={i18n.t('common.select')}
            searchPlaceholder={i18n.t('common.search')}
          >
            {permissionGroupOptions || []}
          </Selector.Popover>
        )}
        {showWelcomeSpace && (
          <DayPicker
            label={i18n.t('employees.bulk_edit.profile.welcome_space.title')}
            field={form.get('regular_access_starts_on')}
            minDate={moment().startOf('day')}
          />
        )}
        {showAnyCustomField && (
          <Box flexDirection="column" gap="s12">
            {customFields?.map((customField) => {
              if (customFieldsSelectionKeys.includes(customField.id.toString())) {
                if (customField.fieldType === 'text' || customField.fieldType === 'long_text') {
                  return (
                    <TextField
                      key={customField.id}
                      label={customField.labelText}
                      onChange={(value) => handleChangeCustomField(customField.id, value)}
                    />
                  )
                }

                if (customField.fieldType === 'cents') {
                  const numberValue = selectedCustomFieldsWithValues[customField.id]

                  return (
                    <NumberField
                      key={customField.id}
                      label={customField.labelText}
                      value={typeof numberValue === 'number' ? numberValue / 100 : undefined}
                      onChange={(value) =>
                        handleChangeCustomField(customField.id, value !== null ? value * 100 : null)
                      }
                    />
                  )
                }

                if (customField.fieldType === 'date') {
                  const dateValue = selectedCustomFieldsWithValues[customField.id]

                  return (
                    <DSDayPicker
                      key={customField.id}
                      valueFormat="backend"
                      format="ui"
                      label={customField.labelText}
                      value={typeof dateValue === 'string' ? dateValue : undefined}
                      onChange={(value) => handleChangeCustomField(customField.id, value)}
                    />
                  )
                }

                if (customField.fieldType === 'single_choice') {
                  const value = selectedCustomFieldsWithValues[customField.id]

                  return (
                    <Selector.Popover
                      key={customField.id}
                      label={customField.labelText}
                      blankLabel={i18n.t('common.empty')}
                      value={value ? [value] : []}
                      onChange={(selectedValue) =>
                        handleChangeCustomField(
                          customField.id,
                          typeof selectedValue === 'string' ? selectedValue : null
                        )
                      }
                      placeholder={i18n.t('common.select')}
                      searchPlaceholder={i18n.t('common.search')}
                    >
                      {customFieldOptions(customField) || []}
                    </Selector.Popover>
                  )
                }
              }
            })}
          </Box>
        )}
      </Box>
    </Modal>
  )
}

export default observer(EditModal)
